import { useState, useEffect } from 'react';
import { Plus, Search, Trash2, Eye, EyeOff, Shield, Check, Lock, Pencil } from 'lucide-react';
import Modal from '../../components/ui/Modal';
import { userAPI, roleAPI } from '../../services/api';

const ALL_PAGES = [
  { key: 'view_dashboard', label: 'Dashboard' },
  { key: 'view_live_data', label: 'Live Data' },
  { key: 'view_weight_history', label: 'Weight History' },
  { key: 'add_weight_entry', label: 'Add Weight Entry' },
  { key: 'export_data', label: 'Export Data' },
  { key: 'manage_users', label: 'Manage Users' },
];

export default function CompanyUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showPagePass, setShowPagePass] = useState({}); // per-page password visibility
  const [editingId, setEditingId] = useState(null);


  const openEdit = (u) => {
    setForm({
      name: u.name,
      email: u.email,
      password: '',
      role_id: u.role_id || '',
      user_type: 'company_user',
      custom_pages: u.permissions || [],
      page_passwords: u.page_passwords || {},
    });
    setEditingId(u.id);
    setModal(true);
  };

  const emptyForm = {
    name: '', email: '', password: '', role_id: '',
    user_type: 'company_user',
    custom_pages: [],      // manually selected pages
    page_passwords: {},    // { page_key: password }
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    Promise.all([userAPI.list(), roleAPI.list()])
      .then(([u, r]) => {
        setUsers(u.data.filter(x => x.user_type === 'company_user'));
        setRoles((r.data || []).filter(role =>
          role.name === 'Manager'
        ));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const selectedRole = roles.find(r => r.id === form.role_id);

  // Role select kelyanantarach role permissions auto-fill
  const onRoleChange = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    setForm(f => ({
      ...f,
      role_id: roleId,
      custom_pages: role
        ? Array.from(new Set([...(f.custom_pages || []), ...(role.permissions || [])]))
        : f.custom_pages,
      page_passwords: f.page_passwords,
    }));
  };

  // Page toggle
  const togglePage = (key) => {
    setForm(f => {
      const has = f.custom_pages.includes(key);
      const newPages = has ? f.custom_pages.filter(p => p !== key) : [...f.custom_pages, key];
      const newPasswords = { ...f.page_passwords };
      if (has) delete newPasswords[key]; // page remove kelyas password kadun taak
      return { ...f, custom_pages: newPages, page_passwords: newPasswords };
    });
  };

  // Page password set
  const setPagePassword = (key, val) => {
    setForm(f => ({
      ...f,
      page_passwords: { ...f.page_passwords, [key]: val }
    }));
  };

  const save = async () => {
    if (!form.name || !form.email || (!editingId && !form.password)) {
      return alert('Name, email and password required');
    }
    setSaving(true);
    try {
      const payload = { ...form, permissions: form.custom_pages };
      if (editingId) {
        if (!payload.password) delete payload.password;
        await userAPI.update(editingId, payload);
      } else {
        await userAPI.create(payload);
      }
      setForm(emptyForm);
      setEditingId(null);
      setModal(false);
      load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    await userAPI.delete(id); load();
  };

  const toggleStatus = async (u) => {
    const newStatus = u.status === 'Active' ? 'Inactive' : 'Active';
    // optimistic UI update
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: newStatus } : x));
    try {
      await userAPI.update(u.id, { status: newStatus });
    } catch (e) {
      // revert if API call fails
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: u.status } : x));
      alert(e.message || 'Failed to update status');
    }
  };


  return (
    <div className="page-container" style={{ padding: 28 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Company Staff Users</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>Manage your company's staff users, roles and page access</p>
        </div>
        <button className="btn-primary" onClick={() => { setEditingId(null); setForm(emptyForm); setModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add User
        </button>
      </div>

      <div style={{ position: 'relative', maxWidth: 320, marginBottom: 20 }}>
        <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search users..."
          style={{ paddingLeft: 30 }}
          autoComplete="off"
          name="user-search-box"
          type="search"
        />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text2)' }}>Loading...</div> : (
          <table>
            <thead>
              <tr><th>User</th><th>Email</th><th>Role</th><th>Page Access</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>No users found</td></tr>
              )}
              {filtered.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent-dim)', border: '1px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                        {u.name[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 500 }}>{u.name}</span>
                    </div>
                  </td>
                  <td className="text2" style={{ fontSize: 12 }}>{u.email}</td>
                  <td>
                    {u.role_name
                      ? <span className="badge badge-purple" style={{ display: 'flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
                        <Shield size={10} /> {u.role_name}
                      </span>
                      : <span className="text3">No role</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(u.permissions || []).slice(0, 3).map(p => {
                        const page = ALL_PAGES.find(x => x.key === p);
                        return page ? (
                          <span key={p} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--accent)30' }}>
                            {page.label}
                          </span>
                        ) : null;
                      })}
                      {(u.permissions || []).length > 3 && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'var(--surface2)', color: 'var(--text3)' }}>
                          +{(u.permissions || []).length - 3} more
                        </span>
                      )}
                      {(u.permissions || []).length === 0 && <span className="text3" style={{ fontSize: 11 }}>None</span>}
                    </div>
                  </td>
                  <td><span className={`badge ${u.status === 'Active' ? 'badge-success' : 'badge-danger'}`}>{u.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <button onClick={() => openEdit(u)} className="btn-ghost" style={{ padding: 4, color: 'var(--text4)' }}>
                            <Pencil size={14} />
                          </button>

                          {/* Active/Inactive toggle */}
                          <div
                            onClick={() => toggleStatus(u)}
                            role="switch"
                            aria-checked={u.status === 'Active'}
                            title={u.status === 'Active' ? 'Click to deactivate' : 'Click to activate'}
                            style={{
                              width: 36,
                              height: 20,
                              borderRadius: 999,
                              background: u.status === 'Active' ? '#10e07a' : 'var(--surface2)',
                              border: `1px solid ${u.status === 'Active' ? '#10e07a' : 'var(--border2)'}`,
                              cursor: 'pointer',
                              position: 'relative',
                              transition: 'background 0.2s ease',
                              flexShrink: 0,
                            }}
                          >
                            <div
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                background: '#fff',
                                position: 'absolute',
                                top: 1,
                                left: u.status === 'Active' ? 17 : 1,
                                transition: 'left 0.2s ease',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              }}
                            />
                          </div>
                        </div>
                      </td>

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Add User Modal ─────────────────────────────────── */}
      <Modal open={modal} onClose={() => { setModal(false); setForm(emptyForm); setEditingId(null); }} title={editingId ? "Edit User" : "Add New User"} width={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Basic Info */}
          <div className="form-group">
            <label>Full Name *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" autoFocus />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="user@company.com" />
          </div>

          <div className="form-group">
            <label>Login Password {editingId ? '(leave blank to keep current)' : '*'}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder={editingId ? 'Leave blank to keep current password' : 'Minimum 6 characters'}
                style={{ paddingRight: 40 }}
                autoComplete="new-password"
              />
              <button onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}>
                {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Role Select */}
          <div className="form-group">
            <label>Assign Role <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
            <select value={form.role_id} onChange={e => onRoleChange(e.target.value)}>
              <option value="">-- Select Role --</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {selectedRole && (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                Selecting a Role will auto-select its pages — you can still change them
              </div>
            )}
          </div>

          {/* Page Access + Page Password */}
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 13 }}>
              Page Access & Passwords
            </label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {ALL_PAGES.map((p, i) => {
                const selected = form.custom_pages.includes(p.key);
                return (
                  <div key={p.key} style={{
                    borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                    background: selected ? 'var(--accent-dim)' : 'transparent',
                    transition: 'background 0.15s',
                  }}>
                    {/* Page row */}
                    <div
                      onClick={() => togglePage(p.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: selected ? 'var(--accent)' : 'var(--surface2)',
                        border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border2)'}`,
                        transition: 'all 0.15s'
                      }}>
                        {selected && <Check size={11} color="#fff" />}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: selected ? 600 : 400, color: selected ? 'var(--text)' : 'var(--text2)', flex: 1 }}>
                        {p.label}
                      </span>
                      {selected && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--accent)' }}>
                          <Lock size={11} /> set Password*
                        </div>
                      )}
                    </div>

                    {/* Page password — fakt selected pages la disel */}
                    {selected && (
                      <div style={{ padding: '0 14px 12px 42px' }}>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showPagePass[p.key] ? 'text' : 'password'}
                            value={form.page_passwords[p.key] || ''}
                            onChange={e => setPagePassword(p.key, e.target.value)}
                            placeholder={`"${p.label}" page la password (optional)`}
                            autoComplete="new-password"
                            name={`page-pwd-${p.key}`}
                            style={{ fontSize: 12, padding: '7px 36px 7px 10px', background: 'var(--card)', borderColor: form.page_passwords[p.key] ? 'var(--accent)' : 'var(--border2)' }}
                            onClick={e => e.stopPropagation()}
                          />
                          <button
                            onClick={e => { e.stopPropagation(); setShowPagePass(s => ({ ...s, [p.key]: !s[p.key] })); }}
                            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}
                          >
                            {showPagePass[p.key] ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        </div>
                        {form.page_passwords[p.key] && (
                          <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 4 }}>
                            ✓ Password set — This page will require this password for access
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              ✓ = Page accessible &nbsp;|&nbsp; Lock icon = Page la extra password set karu shakta (optional)
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button className="btn-secondary" onClick={() => { setModal(false); setForm(emptyForm); setEditingId(null); }}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? (editingId ? 'Saving...' : 'Adding...') : (editingId ? 'Save Changes' : 'Add User')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}